import { Form } from "antd";
import { useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useSelector } from "react-redux";

import { CustomNotification } from "../../../../util/CustomNotification";
import { SuccessMessageConstants } from "../../../../constants/MessageConstants/SuccessMessageConstants";

import type { UploadProps } from "antd";
import type FixMeLater from "../../../../shared/types/fixMeLater.type";
import type { RootState } from "../../../../appRedux/store";

type CustomerClientMasterProps = {
  saving: boolean;
  addRecord: boolean;
  handleAddRecord: (value: boolean) => void;
  rowData: FixMeLater;
  handleClearRowIdAndActionType: () => void;
  handleSubmit: (data: FixMeLater) => void;
};

interface DataObject {
  "Client Name"?: string;
  "Allocated Parking"?: string | number;
  "Contact Name"?: string;
  "Contact Number"?: string | number;
  "Email Address"?: string;
  [key: string]: string | number | undefined;
}

export const useCustomerClientMaster = (props: CustomerClientMasterProps) => {
  /* CONSTANTS */
  const [form] = Form.useForm();
  const formRef = useRef<FixMeLater>(null);

  /* STATES */
  const [formFieldsLoadedToForm, setFormFieldsLoadedToForm] = useState(false);
  /* 
     true -> bulk entry 
     false -> single entry
  */
  const [isBulkEntry, setIsBulkEntry] = useState(false);

  /* CUSTOM HOOKS */

  const { masterFilters } = useSelector(({ common }: RootState) => common);

  /* HELPER FUNCTION */
  const validateExcelData = (data: DataObject[]): boolean => {
    for (let index = 0; index < data.length; index++) {
      const item = data[index];
      const { "Client Name": clientName, "Allocated Parking": allocatedParking, "Contact Name": contactName, "Contact Number": contactNumber, "Email Address": emailAddress } = item;

      /* Client Name: Accepts a-z, A-Z, 0-9, ', - */
      if (!clientName || typeof clientName !== "string" || !/^[a-zA-Z0-9'\- ]+$/.test(clientName)) {
        CustomNotification.showErrorMessage(`Row ${index + 2}: Invalid Client Name: Only letters, apostrophes, and hyphens are allowed.`);
        return false;
      }

      /* Allocated Parking: Accepts only numbers */
      if (typeof allocatedParking !== "number" || allocatedParking < 0) {
        CustomNotification.showErrorMessage(`Row ${index + 2}: Invalid Allocated Parking: Only positive numbers are allowed.`);
        return false;
      }

      /* Contact Name: Accepts a-z, A-Z, ', - */
      if (!contactName || typeof contactName !== "string" || !/^[a-zA-Z'\- ]+$/.test(contactName)) {
        CustomNotification.showErrorMessage(`Row ${index + 2}: Invalid Contact Name: Only letters, apostrophes, hyphens, and spaces are allowed.`);
        return false;
      }

      /* Contact Number: Must be in +91 format with exactly 10 digits */
      if (!contactNumber) {
        CustomNotification.showErrorMessage(`Row ${index + 2}: Contact Number is required.`);
        return false;
      }

      if (typeof contactNumber === "string" && !/^\+91\d{10}$/.test(contactNumber)) {
        CustomNotification.showErrorMessage(`Row ${index + 2}: Invalid Contact Number: Must be in the format +91 followed by a 10-digit number.`);
        return false;
      } else if (typeof contactNumber === "number" && !/^\d{10}$/.test(contactNumber.toString())) {
        CustomNotification.showErrorMessage(`Row ${index + 2}: Invalid Contact Number: Contact Number must be a 10-digit number.`);
        return false;
      }

      /* Email Address: Common email validation */
      if (emailAddress && (typeof emailAddress !== "string" || !/^\S+@\S+\.\S+$/.test(emailAddress))) {
        CustomNotification.showErrorMessage(`Row ${index + 2}: Invalid Email Address: Must be a valid email format.`);
        return false;
      }
    }

    return true;
  };

  const formattedExcelData = (dataObjects: DataObject[]) => {
    return dataObjects?.map((item: DataObject) => {
      let contactNumber = item?.["Contact Number"];
      let allocatedParking = item?.["Allocated Parking"];

      /* Convert number to string if it's a number */
      if (typeof contactNumber === "number") {
        contactNumber = contactNumber.toString();
      }

      /* Add +91 if it's a 10-digit number */
      if (contactNumber?.length === 10) {
        contactNumber = `+91${contactNumber}`;
      }

      if (typeof allocatedParking === "string") {
        const numericValue = Number(allocatedParking);
        if (!isNaN(numericValue)) {
          allocatedParking = numericValue;
        }
      }

      return {
        ...item,
        "Contact Number": contactNumber,
        "Allocated Parking": allocatedParking
      };
    });
  };

  /* HANDLERS */
  const handleBulkEntry = (value: boolean) => {
    setIsBulkEntry(value);
    form.resetFields();
  };

  /* sample excel download */
  const downloadSampleExcel = () => {
    const sampleData = [
      ["Client Name", "Allocated Parking", "Contact Name", "Contact Number", "Email Address"],
      ["Client", "10", "John Doe", "+919999999999", "john@gmail.com"]
    ];

    const ws = XLSX.utils.aoa_to_sheet(sampleData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sample");

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([wbout], { type: "application/octet-stream" }), "SampleExcel.xlsx");
  };

  const handleUpload: UploadProps["beforeUpload"] = (file) => {
    try {
      /* Check if the file type is correct (e.g., Excel files) */
      const isExcelFile = file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" || file.type === "application/vnd.ms-excel";

      if (!isExcelFile) {
        CustomNotification.showErrorMessage("Invalid file type. Please upload an Excel file.");
        return false;
      }

      /* reset field value */
      form.setFieldValue("bulk_entry", null);

      const reader = new FileReader();

      reader.onload = (e: ProgressEvent<FileReader>) => {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as (string | number)[][];

        /* Convert to array of objects with key-value pairs */
        const keys = jsonData[0] as string[];
        const values = jsonData.slice(1);

        const dataObjects: DataObject[] = values.map((row) => {
          const obj: DataObject = {};
          keys.forEach((key, i) => {
            obj[key] = row[i];
          });
          return obj;
        });

        /* formatted contact number 10 digit with add +91 */
        const bulkEntryFormattedData = formattedExcelData(dataObjects);

        /* Validate extracted data */
        if (!validateExcelData(bulkEntryFormattedData)) return false;

        form.setFieldValue("bulk_entry", bulkEntryFormattedData);
        CustomNotification.showInfoMessage(SuccessMessageConstants.FILE_UPLOAD_SUCCESSFUL);
      };

      reader.readAsArrayBuffer(file);

      /* Prevent default upload behavior */
      return false;
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    }
  };

  const handleSubmit = (data: any) => {
    let updatedData;
    if (props.addRecord && isBulkEntry) {
      const formattedData = data?.bulk_entry?.map((item: DataObject) => {
        return {
          client_name: item?.["Client Name"],
          allocated_parking: item?.["Allocated Parking"],
          client_contact: item?.["Contact Name"],
          email_address: item?.["Email Address"],
          mobile_number: item?.["Contact Number"]
        };
      });
      updatedData = { ...data, customer_id: masterFilters?.customerId, customer_location_id: masterFilters?.locationId };
      props.handleSubmit({ ...updatedData, bulk_entry: formattedData });
    } else if (props.addRecord) {
      updatedData = { ...data, customer_id: masterFilters?.customerId, customer_location_id: masterFilters?.locationId };
      props.handleSubmit(updatedData);
    } else {
      updatedData = { ...data, customer_id: props.rowData?.customer_id, customer_location_id: props.rowData?.customer_location_id };
      props.handleSubmit(updatedData);
    }
  };

  /* loading states from multiple hooks added as expression */
  const isOnAddLoading = !props.addRecord;
  const isOnEditLoading = !!props.addRecord;

  /* EFFECTS */
  useEffect(() => {
    if (!props.addRecord && !isOnEditLoading && !formFieldsLoadedToForm) {
      form.setFieldsValue(props.rowData);
      setFormFieldsLoadedToForm(true);
    }

    // eslint-disable-next-line
  }, [isOnEditLoading]);

  return {
    form,
    formRef,
    formFieldsLoadedToForm,
    customerId: masterFilters?.customerId,
    isOnAddLoading,
    isOnEditLoading,
    isBulkEntry,
    handleBulkEntry,
    handleUpload,
    downloadSampleExcel,
    handleSubmit
  };
};
